import type { PortalHelpConfig } from './portalHelpTypes';

export const APPRENANT_HELP: PortalHelpConfig = {
  pageTitle: 'Centre d’aide apprenant',
  pageDescription: 'Guide complet de votre espace : formations, paiements, e-learning, dossier et parrainage.',
  roleLabel: 'Apprenant',
  intro:
    'Ce guide explique pas à pas chaque écran de votre espace apprenant. Lisez la carte correspondant à votre besoin : la plupart des démarches (inscription, paiement, dossier, cours) se font sans contacter le support si vous suivez les étapes ci-dessous.',
  gettingStarted: {
    title: 'Par où commencer ?',
    steps: [
      'Connectez-vous avec votre numéro de téléphone et votre mot de passe.',
      'Ouvrez Formations dans le menu : parcourez les filières ouvertes et notez la cohorte qui vous convient (dates, horaires).',
      'Cliquez sur S’inscrire, remplissez le formulaire et validez : une inscription « en attente » est créée.',
      'Réglez les frais (acompte ou total) via Paiement ; attendez la confirmation avant de fermer la page.',
      'Déposez vos pièces dans Mon dossier (pièce d’identité, photo, diplômes…) le plus tôt possible.',
      'Une fois l’inscription validée par le centre, vos cours apparaissent dans Mes cours et E-learning.',
    ],
  },
  sections: [
    {
      id: 'accueil',
      title: 'Accueil',
      icon: 'dashboard',
      summary:
        'Page d’atterrissage après connexion. Elle résume votre situation : inscriptions en cours, montants à payer, dossier incomplet, prochaines échéances et raccourcis vers les modules les plus utiles.',
      steps: [
        'En haut, vérifiez votre nom et votre photo : si ce n’est pas vous, déconnectez-vous et reconnectez-vous avec le bon compte.',
        'Les cartes colorées indiquent ce qui demande une action (paiement en attente, document refusé, inscription à finaliser). Cliquez directement sur la carte concernée.',
        'La section « Mes inscriptions » liste vos parcours actifs : statut (en attente, active, suspendue), filière, cohorte et solde restant.',
        'Les raccourcis en bas mènent vers Mes cours, Paiement, Mon dossier, E-learning, etc. : utilisez-les plutôt que de chercher dans tout le menu.',
        'Si un chiffre (montant, nombre de documents) vous semble faux, cliquez sur l’icône Actualiser en haut à droite, attendez 5 secondes, puis relisez l’accueil.',
        'Les notifications importantes (paiement reçu, dossier validé) apparaissent aussi dans la cloche en haut à droite : consultez-les régulièrement.',
      ],
      tips: [
        'Consultez l’accueil au moins une fois par semaine pendant votre inscription pour ne manquer aucune demande du centre.',
        'Un statut « en attente de paiement » signifie que votre place n’est pas garantie tant que le règlement n’est pas confirmé.',
      ],
      linkTo: '/app',
      linkLabel: 'Ouvrir l’accueil',
    },
    {
      id: 'mes-cours',
      title: 'Mes cours',
      icon: 'list_alt',
      summary:
        'Liste toutes vos inscriptions liées à des formations. Chaque ligne correspond à un parcours (filière + cohorte) avec son statut administratif et pédagogique.',
      steps: [
        'Ouvrez Mes cours dans le menu latéral : vous voyez une carte ou un tableau par inscription.',
        'Lisez le statut affiché : « En attente » (dossier ou paiement incomplet), « Active » (vous suivez la formation), ou autre statut expliqué sous le libellé.',
        'Cliquez sur une inscription pour ouvrir le détail : filière, cohorte, dates, montant payé et montant restant y sont indiqués.',
        'Si un bouton Payer ou Compléter le dossier apparaît, suivez-le : il vous envoie directement vers la bonne page.',
        'Une inscription « Active » donne accès aux contenus e-learning et aux sessions planifiées pour cette cohorte uniquement.',
        'Vous ne voyez pas une formation alors que vous avez payé ? Vérifiez d’abord le statut ici ; si « En attente » persiste plus de 24 h après paiement, notez la référence de transaction (Portefeuille → Historique complet) avant de contacter le support.',
      ],
      tips: [
        'Une seule inscription active par filière/cohorte est normale : ne recréez pas une inscription si vous en avez déjà une en cours.',
        'Le matricule officiel, lorsqu’il est attribué, apparaît sur le détail de l’inscription ou dans Mon profil.',
      ],
      linkTo: '/app/mes-cours',
      linkLabel: 'Voir mes cours',
    },
    {
      id: 'elearning',
      title: 'E-learning',
      icon: 'play_circle',
      summary:
        'Bibliothèque numérique liée à vos formations : vidéos, PDF, exercices et forums. Accessible dès que votre inscription est active et que le formateur ou l’administration a publié les contenus.',
      steps: [
        'Ouvrez E-learning : les contenus sont regroupés par cours ou par filière selon la configuration du centre.',
        'Utilisez la barre de recherche ou les tags (filtres) pour retrouver une ressource précise (ex. « Module 1 », « Examen »).',
        'Pour une vidéo : cliquez sur la vignette ; la lecture démarre dans la page. Laissez la page ouverte jusqu’à la fin si un suivi de progression est requis.',
        'Pour un PDF : ouvrez-le dans la visionneuse intégrée (pas besoin d’application externe). Utilisez Télécharger seulement si le bouton est visible — certains documents sont en lecture seule.',
        'Pour un exercice : lisez la consigne, déposez votre fichier ou répondez dans le formulaire, puis validez. Un message confirme l’envoi ; vous pourrez voir le statut (envoyé, corrigé) en rouvrant l’exercice.',
        'Forums : ouvrez le fil correspondant au module, rédigez un message clair (objet + question), publiez. Respectez les règles indiquées par le formateur ; les messages peuvent être modérés.',
        'Si une ressource affiche « indisponible » ou une liste vide, votre inscription n’est peut-être pas encore active ou le contenu n’est pas publié : vérifiez Mes cours avant de signaler un problème.',
      ],
      tips: [
        'Connexion instable : téléchargez les PDF autorisés en Wi-Fi pour les consulter hors ligne.',
        'Avant de poster sur un forum, parcourez les messages existants : la réponse y est souvent déjà.',
      ],
      linkTo: '/app/elearning',
      linkLabel: 'Accéder à l’e-learning',
    },
    {
      id: 'formations',
      title: 'Formations & inscription',
      icon: 'school',
      summary:
        'Catalogue public des filières ouvertes aux inscriptions. C’est ici que vous choisissez votre formation, votre cohorte et que vous démarrez officiellement votre dossier.',
      steps: [
        'Ouvrez Formations : parcourez les cartes (libellé, durée, domaine, tarif indicatif).',
        'Cliquez sur une filière pour lire la description complète, les prérequis et les cohortes disponibles (dates de début, places restantes si affichées).',
        'Choisissez la cohorte qui correspond à votre disponibilité : une cohorte fermée ou complète ne permet pas de s’inscrire.',
        'Appuyez sur S’inscrire / Continuer : un formulaire s’ouvre (coordonnées, parfois code parrain). Vérifiez l’orthographe de votre nom — il figurera sur vos documents officiels.',
        'Si vous avez un code parrain ou un QR institutionnel, saisissez-le à cette étape ; il ne pourra pas toujours être ajouté après validation.',
        'À la fin, validez : vous êtes redirigé vers le détail de l’inscription ou vers Paiement selon le parcours configuré.',
        'Une inscription créée apparaît immédiatement dans Mes cours avec le statut « En attente » tant que le paiement et/ou le dossier ne sont pas conformes.',
      ],
      tips: [
        'Photocopiez ou photographiez votre pièce d’identité en amont : format JPG ou PDF, lisible, sans reflet.',
        'Ne créez pas plusieurs inscriptions identiques « par sécurité » : cela complique le traitement administratif.',
      ],
      linkTo: '/app/formations',
      linkLabel: 'Parcourir les formations',
    },
    {
      id: 'paiement',
      title: 'Paiement',
      icon: 'payments',
      summary:
        'Règlement des frais d’inscription (acompte ou montant total). Les modes disponibles dépendent du centre (Mobile Money, carte bancaire, etc.).',
      steps: [
        'Depuis Mes cours ou le détail d’inscription, cliquez sur Payer ou Régler les frais.',
        'Lisez le montant dû (acompte minimum ou solde total) et la devise affichée (CDF, USD…).',
        'Choisissez le mode de paiement proposé : Mobile Money (M-Pesa, Orange, Airtel…), carte ou autre selon les options à l’écran.',
        'Pour Mobile Money : saisissez le numéro du compte qui paiera, confirmez sur votre téléphone lorsque l’opérateur envoie la demande USSD ou l’notification.',
        'Ne fermez pas la page pendant le traitement : attendez l’écran de résultat (succès, échec, en attente de confirmation).',
        'En cas de succès, notez ou capturez la référence de transaction affichée : elle sert de preuve en cas de litige.',
        'Si le statut est « En attente », l’opérateur n’a pas encore confirmé : attendez 15 à 30 minutes, actualisez Portefeuille ou Mes cours.',
        'En cas d’échec, vérifiez le solde de votre compte mobile, le plafond journalier, puis réessayez une seule fois avant de contacter le support avec la référence d’échec.',
      ],
      tips: [
        'Le numéro Mobile Money utilisé pour payer peut être différent de celui de connexion à la plateforme : vérifiez le bon compte avant de valider.',
        'Conservez les SMS de confirmation de votre opérateur en plus de la référence affichée sur la plateforme.',
      ],
    },
    {
      id: 'documents',
      title: 'Mon dossier',
      icon: 'folder',
      summary:
        'Dépôt des pièces administratives exigées par le centre (identité, photo, diplômes, attestations…). Chaque document a un statut visible en temps réel.',
      steps: [
        'Ouvrez Mon dossier : la liste indique chaque pièce demandée (libellé, obligatoire ou optionnelle).',
        'Préparez vos fichiers : PDF ou JPG, taille raisonnable, texte lisible, document entier (pas de recadrage excessif).',
        'Cliquez sur Déposer ou Remplacer, sélectionnez le fichier sur votre appareil, puis confirmez l’envoi.',
        'Le statut passe à « En attente de validation » : un agent du centre va contrôler la pièce sous 24 à 72 h ouvrées en général.',
        'Statut « Validé » : rien à faire. Statut « Refusé » ou « À corriger » : lisez le motif affiché, corrigez le document et téléversez une nouvelle version.',
        'Répétez pour chaque pièce jusqu’à ce que toutes les lignes obligatoires soient vertes / validées.',
        'Un dossier complet est souvent requis avant l’accès définitif aux cours présentiels ou à l’attestation finale.',
      ],
      tips: [
        'Pour une photo d’identité : fond neutre, visage dégagé, sans filtre.',
        'Si un document est refusé sans explication claire, renvoyez une version plus nette avant de contacter le support.',
      ],
      linkTo: '/app/documents',
      linkLabel: 'Gérer mon dossier',
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: 'notifications',
      summary:
        'Messages automatiques de la plateforme : confirmations de paiement, validation de dossier, rappels, résultats et annonces du centre.',
      steps: [
        'Cliquez sur la cloche en haut à droite pour l’aperçu rapide (derniers messages non lus).',
        'Pour l’historique complet, ouvrez Notifications dans le menu ou le lien « Voir toutes les notifications ».',
        'Chaque message indique un titre, un texte et une date : lisez-le entièrement avant de le marquer comme lu.',
        'Utilisez Marquer comme lu sur un message traité, ou Tout marquer comme lu pour vider la liste des non lus.',
        'Dans les préférences (icône engrenage du panneau notifications), activez ou désactivez les catégories (paiements, sécurité, promotions…).',
        'Heures silencieuses : si disponible, définissez une plage horaire sans alertes push (ex. 22h–7h).',
        'Une notification de paiement reçu confirme que l’administration a enregistré votre transaction : croisez avec Portefeuille → Historique complet si besoin.',
      ],
      tips: [
        'Autorisez les notifications du navigateur ou de l’application mobile pour ne pas manquer un rappel de paiement.',
        'Les messages de sécurité (nouvelle connexion, changement de mot de passe) ne doivent pas être ignorés.',
      ],
      linkTo: '/app/notifications',
      linkLabel: 'Mes notifications',
    },
    {
      id: 'parrainage',
      title: 'Parrainage',
      icon: 'groups',
      summary:
        'Programme de recommandation : vous partagez un code personnel ; lorsqu’un filleul s’inscrit et remplit les conditions, une commission peut vous être créditée.',
      steps: [
        'Ouvrez Parrainage : votre code unique et/ou lien de parrainage s’affichent en haut.',
        'Copiez le code ou partagez le lien (WhatsApp, SMS, email) en expliquant à votre filleul qu’il doit le saisir lors de l’inscription.',
        'Le QR code, s’il est proposé, peut être scanné directement depuis la page d’inscription publique ou l’application.',
        'La liste des filleuls indique qui a commencé une inscription, qui l’a finalisée et le statut de la commission (en attente, validée, payée).',
        'Les commissions ne sont pas instantanées : elles suivent les règles du centre (inscription payée, dossier validé, délai de rétractation éventuel).',
        'Les montants crédités apparaissent dans Portefeuille ; vous pouvez alors demander un retrait selon les seuils affichés.',
      ],
      tips: [
        'Le code doit être saisi au moment de l’inscription : un filleul déjà inscrit ne peut généralement pas être rattaché rétroactivement.',
        'Ne partagez pas de codes falsifiés : seul votre code personnel affiché sur cette page est valide.',
      ],
      linkTo: '/app/parrainage',
      linkLabel: 'Mon parrainage',
    },
    {
      id: 'wallet',
      title: 'Portefeuille',
      icon: 'account_balance_wallet',
      summary:
        'Compte virtuel des commissions parrainage : solde disponible, historique des crédits/débits et demandes de retrait.',
      steps: [
        'Ouvrez Portefeuille : le solde disponible est affiché en évidence (devise du centre).',
        'Consultez l’historique : chaque ligne indique la date, le libellé (commission filleul, retrait, ajustement) et le montant.',
        'Pour retirer : cliquez sur Demander un retrait, saisissez le montant (respectez le minimum affiché) et le moyen de paiement autorisé.',
        'Confirmez : le statut passe à « En cours de traitement » ; l’administration valide ou refuse sous quelques jours ouvrés.',
        'Un retrait refusé est crédité à nouveau sur votre solde : lisez le motif dans l’historique ou les notifications.',
        'Le solde « en attente » ou « bloqué », s’il apparaît, correspond à des commissions pas encore libérées par les règles parrainage.',
      ],
      tips: [
        'Vérifiez que vos coordonnées bancaires ou Mobile Money dans Mon profil sont à jour avant un retrait.',
        'Un seul retrait en cours à la fois est habituel : attendez le traitement avant d’en lancer un autre.',
      ],
      linkTo: '/app/wallet',
      linkLabel: 'Mon portefeuille',
    },
    {
      id: 'excellence',
      title: 'Excellence & employabilité',
      icon: 'emoji_events',
      summary:
        'Programme de valorisation des meilleurs parcours : classements, scores, périodes de calcul et propositions d’orientation ou de stages.',
      steps: [
        'Ouvrez Excellence : la période en cours et vos critères d’éligibilité y sont décrits (moyenne, assiduité, etc.).',
        'Consultez votre rang ou score si vous êtes classé ; sinon un message explique pourquoi (période non commencée, données insuffisantes).',
        'Lisez les onglets ou sections Orientation, Stages ou Partenaires si le centre les a activés.',
        'Une proposition reçue (notification + email) doit être acceptée ou refusée dans les délais indiqués sur l’écran.',
        'Les classements sont recalculés périodiquement : un mauvais résultat temporaire peut être rattrapé sur la période suivante.',
      ],
      tips: [
        'L’assiduité (présences) compte souvent autant que les notes : consultez vos sessions dans Mes cours.',
        'Le matricule et le dossier à jour sont requis pour apparaître dans les classements officiels.',
      ],
      linkTo: '/app/excellence',
      linkLabel: 'Programme excellence',
    },
    {
      id: 'wallet-historique',
      title: 'Historique des transactions',
      icon: 'receipt_long',
      summary:
        'Historique complet de vos paiements et mouvements portefeuille : dates, montants, modes, références et statuts.',
      steps: [
        'Ouvrez Portefeuille puis l’onglet Historique complet : toutes les transactions sont listées par date (plus récentes en premier).',
        'Chaque ligne indique l’inscription concernée, le montant, le mode (Mobile Money, portefeuille…) et la référence unique.',
        'Téléchargez le PDF d’un reçu lorsque le bouton est disponible.',
        'Utilisez cette page pour vérifier qu’un paiement « réussi » côté opérateur a bien été enregistré côté centre.',
        'Avant de contacter le support pour un paiement manquant, notez : date, montant, référence opérateur, référence plateforme (si affichée), numéro Mobile Money utilisé.',
      ],
      tips: [
        'Exportez ou capturez l’écran d’une transaction réussie pour vos archives personnelles.',
        'Un paiement en double le même jour avec deux références différentes doit être signalé rapidement au support avec les deux preuves.',
      ],
      linkTo: '/app/wallet?tab=historique',
      linkLabel: 'Historique complet',
    },
    {
      id: 'profil',
      title: 'Mon profil',
      icon: 'person',
      summary:
        'Identité, coordonnées, photo et sécurité du compte. Des informations exactes évitent les blocages de dossier ou de paiement.',
      steps: [
        'Ouvrez Mon profil : vérifiez prénom, nom, email et téléphone. Corrigez toute erreur avant de déposer des documents officiels.',
        'Pour modifier un champ : éditez, enregistrez, attendez le message de confirmation.',
        'Photo de profil : téléversez une image claire (JPG/PNG) ; elle apparaît dans l’en-tête et parfois sur les listes de présence.',
        'Mot de passe : utilisez Changer le mot de passe ; choisissez au moins 8 caractères avec lettres et chiffres ; ne réutilisez pas un mot de passe d’un autre site.',
        'Après changement de mot de passe, reconnectez-vous sur vos autres appareils.',
        'Si vous avez perdu accès à votre téléphone de connexion, contactez le support avec une pièce d’identité — la réinitialisation se fait côté administration.',
      ],
      tips: [
        'L’email sert aux reçus et aux orientations excellence : utilisez une adresse que vous consultez vraiment.',
        'Ne communiquez jamais votre mot de passe, même à un « agent » : le support ne vous le demandera pas.',
      ],
      linkTo: '/app/profil',
      linkLabel: 'Modifier mon profil',
    },
  ],
  faqs: [
    {
      question: 'Je ne vois pas ma formation après paiement.',
      answer:
        '1) Ouvrez Portefeuille → Historique complet et vérifiez que la transaction est « Réussie » avec une référence. 2) Actualisez Mes cours. 3) Si le statut reste « En attente » plus de 24 h, envoyez au support : référence plateforme + capture SMS opérateur + numéro Mobile Money utilisé.',
    },
    {
      question: 'Comment obtenir mon matricule ?',
      answer:
        'Le matricule est généré après validation de l’inscription et contrôle du dossier par l’administration. Consultez le détail dans Mes cours ou Mon profil. S’il n’apparaît pas alors que tout est validé, précisez votre nom et cohorte au support.',
    },
    {
      question: 'Puis-je changer de cohorte ?',
      answer:
        'Un changement de cohorte implique l’administration (places disponibles, calendrier). Préparez votre inscription actuelle, la cohorte souhaitée et la raison ; le support ou le secrétariat traitera la demande manuellement.',
    },
    {
      question: 'Mon document est refusé sans explication.',
      answer:
        'Renvoyez une photo plus nette (bords visibles, sans flash). Vérifiez que le type de document correspond à la ligne demandée (ex. relevé de notes vs pièce d’identité). Si le refus persiste, indiquez le nom exact de la pièce refusée au support.',
    },
    {
      question: 'Je n’arrive pas à me connecter.',
      answer:
        'Vérifiez le numéro au format attendu (indicatif pays). Utilisez Mot de passe oublié sur la page de connexion. Si le compte est bloqué après plusieurs essais, attendez 15 minutes ou contactez le support.',
    },
  ],
};
